import { useQuery } from "@tanstack/react-query";
import { IconInfo } from "../../../shared/components/icons";
import { estimatePaymentDays } from "../api";

type Props = {
  amountTzs?: number | null;
  amountCents?: number | null;
  customerId?: number | null;
  contractId?: number | null;
  /** Compact single-line for tables / wizard */
  compact?: boolean;
};

/**
 * Live preview: how many PAYG days this amount would unlock for the customer.
 */
export function DaysEstimateHint({
  amountTzs,
  amountCents,
  customerId,
  contractId,
  compact = false,
}: Props) {
  const cents =
    amountCents != null && Number.isFinite(amountCents)
      ? Math.round(amountCents)
      : amountTzs != null && Number.isFinite(amountTzs)
        ? Math.round(amountTzs * 100)
        : null;
  const enabled = cents != null && cents > 0;

  const q = useQuery({
    queryKey: ["payment-days-estimate", cents, customerId ?? null, contractId ?? null],
    queryFn: () =>
      estimatePaymentDays({
        amountCents: cents!,
        customerId: customerId ?? undefined,
        contractId: contractId ?? undefined,
      }),
    enabled,
    staleTime: 15_000,
    retry: 1,
  });

  if (!enabled) return null;
  if (q.isLoading) {
    return (
      <p className="pg-text-muted" style={{ margin: compact ? "4px 0 0" : "8px 0 0", fontSize: 13 }}>
        Calculating days…
      </p>
    );
  }
  if (q.isError || !q.data) return null;

  const days = q.data.estimated_days;
  const shortfall = q.data.remaining_for_token_tzs ?? (q.data.shortfall_cents != null ? q.data.shortfall_cents / 100 : 0);
  const headline = q.data.fully_paid
    ? "Completes the loan — permanent unlock"
    : shortfall > 0 && days === 0
      ? `No token yet — need ≈ TZS ${Math.round(shortfall).toLocaleString()} more`
      : days === 1
        ? "≈ 1 day of PAYG power"
        : `≈ ${days} days of PAYG power`;

  return (
    <div
      className="pg-days-estimate"
      role="status"
      style={{
        marginTop: compact ? 4 : 8,
        padding: compact ? "6px 8px" : "8px 10px",
        borderRadius: 6,
        background: "rgba(15, 118, 110, 0.08)",
        border: "1px solid rgba(15, 118, 110, 0.22)",
        fontSize: 13,
        lineHeight: 1.35,
      }}
    >
      <strong style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <IconInfo size={14} /> {headline}
      </strong>
      {!compact ? (
        <div className="pg-text-muted" style={{ marginTop: 4 }}>
          {q.data.message}
        </div>
      ) : null}
    </div>
  );
}
